import { logError, logFatal, logInformation, logWarning } from '@core/logging';
import { ApiResponse, BaseApiResponse, ChatSettings, WebinarDataWithAssets } from '@core/swagger-client';
import {
    ChatLinkPolicy,
    DisconnectReason,
    Exam,
    ExamResult,
    LiveStatus,
    Participant,
    ParticipantAnswers,
    ParticipantSession,
    Poll,
    PollVoteSlim,
    SubtitlesTrack,
    Survey
} from '@core/types';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { Action } from '@reduxjs/toolkit';
import {
    onDemandChatSettingsUpdated,
    onDemandCurrentPollsUpdated,
    OnDemandData,
    onDemandDataConnectionStatusUpdated,
    onDemandDataUpdated,
    onDemandExamAnswersUpdated,
    onDemandExamUpdated,
    onDemandMeUpdated,
    onDemandMyPollVotes,
    onDemandRefreshData,
    onDemandResumeCompletedReceived,
    onDemandResumePositionReceived,
    onDemandServerTimeUpdated,
    onDemandSubtitlesUpdated,
    onDemandSurveyAnswersUpdated,
    onDemandSurveyUpdated,
    onDemandWebinarDataUpdated
} from '../slices/on-demand-webinar-slice';
import { chatLinkPolicyReceived } from '../slices/chat-link-policy-slice';
import { store } from '../store';

interface OnDemandState {
    me: Participant;
    data: WebinarDataWithAssets;
    onDemandData: OnDemandData;
    startedOn: Date | null;
    endedOn: Date | null;
    survey: Survey;
    surveyAnswers: ParticipantAnswers;
    exam: Exam;
    examAnswers: ParticipantAnswers;
    pollVotes: PollVoteSlim[];
    /** Missing while a backend from before the percentages-only setting is still live. */
    currentPolls?: Poll[];
    chatSettings: ChatSettings;
    subtitlesTracks: SubtitlesTrack[];
    resumePosition?: number | null;
    /**
     * True when the viewer already finished this on-demand — meaningful even when resumePosition is
     * null, distinguishing "already completed" from "nothing to resume from yet". See
     * OnDemandResumeInfo.Completed on the backend.
     */
    resumeCompleted?: boolean;
}

export class OnDemandWebinarConnection {
    /** Set once by provideStoreRuntime; the hubs live at the root of the backend origin. */
    hubBaseUrl = '';

    isEditor: boolean = false;
    isConnected: boolean = false;

    private disconnectReason?: DisconnectReason;
    private error?: BaseApiResponse;
    private connection?: HubConnection;

    private participantSession?: ParticipantSession;
    private lastPlayPosition?: number;

    private accessToken?: string;

    constructor() {
        if (globalThis.document) {
            globalThis.document.addEventListener('visibilitychange', () => {
                if (!this.isConnected || !this.connection) return;
                if (!globalThis.document.hidden) {
                    if (this.connection.state === HubConnectionState.Connected) {
                        // Verify if we're still connected
                        this.connection
                            .invoke('Ping')
                            .then(() => {
                                // Should we do something?
                            })
                            .catch(() => {
                                // Should we do something?
                            });
                    }
                }
            });
        }
    }

    dispatch(action: Action): void {
        store.dispatch(action);
        return;
    }

    connect(accessToken: string): Promise<boolean> {
        if (this.accessToken === accessToken) {
            // We are already connected with this access token
            return Promise.resolve(true);
        }

        this.accessToken = accessToken;
        this.lastPlayPosition = undefined;
        this.participantSession = undefined;

        // If we have a connection, close it before we start a new connection
        if (this.connection) {
            this.disconnect();
        }

        this.connection = new HubConnectionBuilder()
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) => {
                    return 500; // Retry every 500ms, regardless of the retry count or elapsed time.  We will handle the disconnect logic ourselves in "onreconnecting" and "onclose" events
                }
            })
            .configureLogging({
                log: (logLevel, message) => {
                    if (message.indexOf('access_token') !== -1) {
                        // Remove the access token from the log message to avoid leaking it in the logs
                        message = message.replace(/access_token=([^&]+)/, 'access_token=***');
                    }

                    switch (logLevel) {
                        case LogLevel.Information:
                            logInformation('[OnDemandConnection] {message}', { message });
                            break;

                        case LogLevel.Warning:
                            logWarning('[OnDemandConnection] {message}', { message });
                            break;

                        case LogLevel.Error:
                            logError('[OnDemandConnection] {message}', { message });
                            break;

                        case LogLevel.Critical:
                            logFatal('[OnDemandConnection] {message}', { message });
                            break;
                    }
                }
            })
            .withUrl(`${this.hubBaseUrl}/on-demand-webinar`, { accessTokenFactory: () => accessToken })
            .build();

        // Make a copy of the connection so we can perform some reference checking on the event dispatching
        const connection = this.connection;
        connection.onclose((error?) => {
            if (!this.connection || this.connection === connection) {
                this.dispatch(
                    onDemandDataConnectionStatusUpdated({
                        status: this.disconnectReason === DisconnectReason.Error ? LiveStatus.Error : LiveStatus.Offline,
                        error: this.error || { statusCode: 500, success: false },
                        disconnectReason: this.disconnectReason
                    })
                );
            }
        });
        connection.onreconnecting((error?) => {
            if (this.connection !== connection) return;
            this.dispatch(
                onDemandDataConnectionStatusUpdated({
                    status: this.disconnectReason === DisconnectReason.Error ? LiveStatus.Error : LiveStatus.Offline,
                    error: this.error || { statusCode: 500, success: false },
                    disconnectReason: this.disconnectReason
                })
            );
        });
        connection.onreconnected((connectionId?) => {
            if (this.connection !== connection) return;
            this.dispatch(onDemandDataConnectionStatusUpdated({ status: LiveStatus.Live }));
        });

        connection.on('ServerTime', (serverTime: string) => this.dispatch(onDemandServerTimeUpdated(serverTime)));
        connection.on('SubtitlesTracksChanged', (tracks: SubtitlesTrack[]) => this.dispatch(onDemandSubtitlesUpdated(tracks)));
        connection.on('OnDemandDataUpdated', (data: OnDemandData) => {
            // If we're an editor, do not update the data but refresh it instead
            if (this.isEditor) {
                this.dispatch(onDemandRefreshData());
            } else {
                this.dispatch(onDemandDataUpdated(data));
            }
        });
        connection.on('OnDemandState', (state: OnDemandState) => {
            this.dispatch(onDemandResumePositionReceived(state.resumePosition ?? null));
            this.dispatch(onDemandResumeCompletedReceived(state.resumeCompleted ?? false));
            this.dispatch(onDemandChatSettingsUpdated(state.chatSettings));
            this.dispatch(onDemandWebinarDataUpdated(state.data));
            this.dispatch(onDemandDataUpdated(state.onDemandData));
            this.dispatch(onDemandMeUpdated(state.me));
            this.dispatch(onDemandSurveyUpdated(state.survey));
            this.dispatch(onDemandSurveyAnswersUpdated(state.surveyAnswers));
            this.dispatch(onDemandExamUpdated(state.exam));
            this.dispatch(onDemandExamAnswersUpdated(state.examAnswers));
            this.dispatch(onDemandMyPollVotes(state.pollVotes));
            this.dispatch(onDemandCurrentPollsUpdated(state.currentPolls ?? []));
            this.dispatch(onDemandSubtitlesUpdated(state.subtitlesTracks));
        });
        connection.on('ChatLinkPolicy', (policy: ChatLinkPolicy) => this.dispatch(chatLinkPolicyReceived(policy)));
        connection.on('Disconnect', (reason: DisconnectReason, error?: BaseApiResponse) => {
            this.isConnected = false;
            this.disconnectReason = reason;
            this.error = error;

            connection.stop().then(() => {
                // Should we do something?
            });
        });

        const connectionCheckInterval = setInterval(() => {
            if (this.connection !== connection || !this.isConnected) {
                clearInterval(connectionCheckInterval);
                return;
            }

            if (
                this.connection.state === HubConnectionState.Connected ||
                this.connection.state === HubConnectionState.Connecting ||
                this.connection.state === HubConnectionState.Reconnecting
            ) {
                return;
            }

            this.connection.start().then(
                () => {
                    this.dispatch(onDemandDataConnectionStatusUpdated({ status: LiveStatus.Live }));
                },
                (reason) => {
                    this.dispatch(
                        onDemandDataConnectionStatusUpdated({
                            status: LiveStatus.Offline,
                            error: this.error || {
                                statusCode: 500,
                                success: false,
                                message: reason?.message || 'Connection failed'
                            },
                            disconnectReason: this.disconnectReason
                        })
                    );
                }
            );
        }, 1000);

        this.disconnectReason = undefined;
        this.error = undefined;
        this.isConnected = true;
        return new Promise<boolean>((resolve, reject) => {
            connection
                .start()
                .then(() => {
                    if (this.connection !== connection) {
                        resolve(false);
                        return;
                    }
                    this.dispatch(onDemandDataConnectionStatusUpdated({ status: LiveStatus.Live }));
                    resolve(true);
                })
                .catch((reason) => {
                    this.dispatch(
                        onDemandDataConnectionStatusUpdated({
                            status: LiveStatus.Offline,
                            error: this.error || {
                                statusCode: 500,
                                success: false,
                                message: reason?.message || 'Connection failed'
                            },
                            disconnectReason: this.disconnectReason
                        })
                    );
                    resolve(false);
                });
        });
    }

    updateCurrentVideoTime(time: number): Promise<ApiResponse<boolean>> {
        if (!this.connection) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }

        if (this.lastPlayPosition === time) {
            // Nothing changed
            return Promise.resolve({ statusCode: 200, success: true, data: true });
        }

        return new Promise((resolve, reject) => {
            if (!this.connection) {
                resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
                return;
            }

            let createNewSession = false;
            if (!this.lastPlayPosition || !this.participantSession) {
                createNewSession = true;
            } else {
                const diff = time - this.lastPlayPosition;
                if (diff < 0 || diff > 10) {
                    createNewSession = true;
                } else if (diff < 1) {
                    // If less than 1 second, do not create a new session or update playback
                    return;
                }
            }
            this.lastPlayPosition = time;

            if (createNewSession || !this.participantSession) {
                this.connection
                    .invoke('StartPlayback', time)
                    .then((session: ApiResponse<ParticipantSession>) => {
                        if (session.success) {
                            this.participantSession = session.data;
                            resolve({ statusCode: 200, success: true, data: true });
                        } else {
                            reject(session);
                        }
                    })
                    .catch(reject);
            } else {
                this.connection.invoke('UpdatePlayback', this.participantSession.sessionId, time).then(resolve).catch(reject);
            }
        });
    }

    voteOnPoll(pollId: string, optionId: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('VoteOnPoll', pollId, optionId);
    }

    submitSurveyAnswer(questionId: string, openAnswer: string | null, selectedAnswers: string[] | null): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('SubmitSurveyAnswer', questionId, openAnswer, selectedAnswers);
    }

    retryExam(): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('RetryExam');
    }

    submitExamAnswer(questionId: string, openAnswer: string | null, selectedAnswers: string[] | null): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('SubmitExamAnswer', questionId, openAnswer, selectedAnswers);
    }

    getExamResult(): Promise<ApiResponse<ExamResult>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('GetExamResult');
    }

    sendChatMessage(channelId: string, message: string): Promise<ApiResponse<boolean>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('SendChatMessage', channelId, message);
    }

    refreshOnDemandData(): Promise<ApiResponse<OnDemandData>> {
        if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
            return Promise.resolve({ statusCode: 500, success: false, message: 'Connection not established', errorCode: 'NotConnected' });
        }
        return this.connection.invoke('RefreshOnDemandData');
    }

    disconnect(): void {
        if (!this.connection) {
            return;
        }

        // Make sure a connect (while disconnecting) causes a new instance to be created
        const connection = this.connection;
        this.connection = undefined;

        connection
            .stop()
            .then(() => {
                // TODO: Should we dispatch an event?  Pretty sure "onclose" will do that
            })
            .catch(() => {
                // TODO: Should we do something here or just ignore it?
            });
    }
}
